<template>
  <Modal ref="modalRef" @after-close="onAfterClose">
    <template #body>
      <View direction="column" gap="md" padding-block="md" class="confirmation">
        <ShowingIntentStep v-if="isIntentStep" v-model="intent" :renter-label="renterLabel" />

        <template v-else>
          <Text variant="h2" align="center">
            Please inform the applicants about the reason for canceling the showing
          </Text>

          <Text variant="body-large" color="secondary" align="center">
            The Renter will receive your explanation. After canceling, Tallo will cease all communication and scheduling
            with this renter.
          </Text>

          <Textarea
            class="cancel-reason-input"
            placeholder="Leave your feedback (optional)"
            rows="4"
            v-model="cancelReasonInput"
          />

          <Hint v-if="fairHousingViolationReason" icon="alert-triangle" type="error">
            <View direction="column" gap="sm">
              <Text variant="label-medium">This may violate fair housing laws. Consider rephrasing it.</Text>
              <Text variant="body-medium">{{ fairHousingViolationReason }}</Text>
            </View>
          </Hint>

          <Hint v-if="!fairHousingViolationReason" icon="sparkle" type="info">
            <View direction="column" gap="sm">
              <Text variant="body-medium">
                Tallo automatically checks your response for fair housing compliance and will provide feedback if
                potential violations are found.
              </Text>
            </View>
          </Hint>
        </template>
      </View>
    </template>

    <template #footer>
      <template v-if="isIntentStep">
        <Button variant="ghost" @click="close">Cancel</Button>
        <Button :disabled="!intent" @click="continueWithIntent">Continue</Button>
      </template>

      <template v-else>
        <Button variant="ghost" @click="goBackToIntent">Back</Button>
        <Button @click="cancelShowing" :loading="isLoading">Cancel showing</Button>
      </template>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { AxiosError } from 'axios';
import { computed, ref } from 'vue';

import { Button, Modal, Textarea, View, Text, useAlertsStore, Hint } from '@tallo/design-system';

import { ShowingActionIntent } from '../interfaces/showing-action-intent.enum';
import { useCancelShowing } from '../mutations/cancel-showing.mutation';
import ShowingIntentStep from './showing-intent-step.vue';

const props = defineProps<{ showingId: string; renterLabel?: string }>();

const emit = defineEmits<{ reschedule: [] }>();

const alertsStore = useAlertsStore();
const { mutateAsync, isLoading } = useCancelShowing();

const modalRef = ref<InstanceType<typeof Modal> | null>(null);
const cancelReasonInput = ref('');
const fairHousingViolationReason = ref('');
const step = ref<'intent' | 'reason'>('intent');
const intent = ref<ShowingActionIntent | null>(null);
const isReschedulePending = ref(false);

const isIntentStep = computed(() => step.value === 'intent');
const renterLabel = computed(() => props.renterLabel || 'this renter');

function cancelShowing() {
  mutateAsync({
    showingId: props.showingId,
    cancelDto: {
      cancelReason: cancelReasonInput.value,
    },
  })
    .then(() => {
      fairHousingViolationReason.value = '';
      close();
    })
    .catch((error) => {
      if (error instanceof AxiosError) {
        switch (error.response?.status) {
          case 406:
            fairHousingViolationReason.value = error?.response?.data?.message?.message;
            break;

          default:
            fairHousingViolationReason.value = '';
            alertsStore.catchError(error);
            break;
        }
      } else {
        fairHousingViolationReason.value = '';
        alertsStore.catchError(error);
      }
    });
}

function open() {
  fairHousingViolationReason.value = '';
  modalRef.value?.show();
}

function close() {
  modalRef.value?.close();
}

// Handing off to the reschedule modal is deferred until this one has finished closing,
// so the two modals never overlap and fight over the focus trap
function onAfterClose() {
  cancelReasonInput.value = '';
  step.value = 'intent';
  intent.value = null;

  if (isReschedulePending.value) {
    isReschedulePending.value = false;
    emit('reschedule');
  }
}

function continueWithIntent() {
  if (intent.value === ShowingActionIntent.RESCHEDULE) {
    isReschedulePending.value = true;
    close();

    return;
  }

  step.value = 'reason';
}

function goBackToIntent() {
  fairHousingViolationReason.value = '';
  intent.value = null;
  step.value = 'intent';
}

defineExpose({
  open,
  close,
});
</script>

<style scoped lang="scss">
.confirmation {
  max-width: 30rem;
}

.cancel-reason-input {
  width: 100%;
}
</style>
